import { config } from '../config';
import { logout } from '../utils/token';

/** Logout via legacy admin so 401 acts as logout; redirect to login with session-expired message. */
const sessionExpiredUrl = `${config.NEW_ADMIN_UI_URL}/login?showTimeoutMessage=true`;

/**
 * Throws an appropriate error based on HTTP status code
 * @param status - HTTP status code
 * @param context - Context for the error message (e.g., "market overview")
 */
export function throwHttpError(status: number, context = 'request'): never {
  if (status === 401) {
    logout(sessionExpiredUrl);
    throw new Error(`Authentication failed (${status}). Please log in again.`);
  }

  if (status === 403) {
    throw new Error(`Access denied (${status}). You do not have permission to access this resource.`);
  }

  if (status === 404) {
    throw new Error(`Not found (${status}). The requested ${context} was not found.`);
  }

  if (status >= 400 && status < 500) {
    throw new Error(`Request failed (${status}). Unable to load ${context}.`);
  }

  if (status >= 500) {
    throw new Error(`Server error (${status}). The API server is experiencing issues. Please try again later.`);
  }

  throw new Error(`Unknown error (${status}). Unable to load ${context}.`);
}

/**
 * Handles fetch response errors with consistent messaging
 * @param response - Fetch Response object
 * @param resourceName - Name of the resource being fetched (for error messages)
 */
export function handleResponseError(response: Response, resourceName = 'data'): void {
  if (response.ok) return;

  throwHttpError(response.status, `Failed to fetch ${resourceName}`);
}

/**
 * Gets a user-friendly error message from an error object
 * Prevents exposing internal error details in production
 * @param error - Error object or unknown
 * @param defaultMessage - Default message if error cannot be safely displayed
 * @returns User-friendly error message
 */
export function getUserFriendlyError(error: unknown, defaultMessage = 'An error occurred. Please try again.'): string {
  if (process.env.NODE_ENV === 'development') {
    return error instanceof Error ? error.message : defaultMessage;
  }

  if (error instanceof Error) {
    const message = error.message;
    const safeMessages = [
      'Authentication token not found',
      'Account not found or not accessible',
      'Failed to fetch',
      'Failed to load',
      'Failed to save',
      'Failed to delete',
      'Failed to update',
      'Please log in again',
      'You do not have permission',
      'was not found',
      'Please try again later',
      'No user data found in response',
    ];

    if (safeMessages.some((safe) => message.includes(safe))) {
      return message;
    }
  }

  return defaultMessage;
}
